import { CopyOutlined } from '@ant-design/icons-vue'
import { Button, Image, message, Tag } from 'ant-design-vue'
import { get, isFunction, omit } from 'lodash-es'

import { Text } from '#/components'
import { dayjs, fallbackImg } from '#/utils'

// 统一注册项目内通用 Cell 渲染器，业务表格通过 cellRender.name 复用。
export function registerCellRenderers(vxeUI: any) {
  /**
   * 解决 vxe-table 在热更新时重复注册 Cell 渲染器的问题。
   */
  vxeUI.renderer.forEach((_item: any, key: string) => {
    if (key.startsWith('Cell')) {
      vxeUI.renderer.delete(key)
    }
  })

  // 图片单元格，统一兜底占位图。
  vxeUI.renderer.add('CellImage', {
    renderTableDefault(_renderOpts: any, params: any) {
      const { column, row } = params
      return h(Image, { fallback: fallbackImg, src: row[column.field] })
    },
  })

  // 链接按钮单元格，保留调用方传入的 Button props。
  vxeUI.renderer.add('CellLink', {
    renderTableDefault(renderOpts: any) {
      const { props } = renderOpts
      return h(
        Button,
        { size: 'small', type: 'link', ...props },
        { default: () => props?.text },
      )
    },
  })

  // 时间单元格，沿用项目 dayjs 扩展的 formatDate。
  vxeUI.renderer.add('CellTime', {
    renderTableDefault(renderOpts: any, params: any) {
      const { props } = renderOpts
      const value = params.row[params.column.field]
      return value ? dayjs(value).formatDate(props?.format) : '-'
    },
  })

  // 可复制文本单元格，使用 vxe 内置 clipboard 保持表格内行为一致。
  vxeUI.renderer.add('CellCopy', {
    renderTableDefault(_renderOpts: any, params: any) {
      const { column, row } = params
      const value = row[column.field]
      if (!value) {
        return '-'
      }

      return (
        <div
          class="cursor-pointer"
          onClick={() => {
            vxeUI.clipboard.copy(value)
            message.success('复制成功')
          }}
        >
          <span>{value}</span>
          <Button size="small" type="text">
            <CopyOutlined />
          </Button>
        </div>
      )
    },
  })

  // Tag 单元格，支持 options 内直接透传 color 等 Tag 属性。
  vxeUI.renderer.add('CellTag', {
    renderTableDefault({ props }: any, { column, row }: any) {
      const value = get(row, column.field)
      const options = props?.options ?? []
      const index = options.findIndex((item: any) => item.value === value)
      const option = index === -1 ? null : options[index]

      const colors = [
        'processing',
        'success',
        'error',
        'warning',
        'magenta',
        'red',
        'volcano',
        'orange',
        'gold',
        'lime',
        'green',
        'cyan',
        'blue',
        'geekblue',
        'purple',
      ]
      const tagProps = {
        ...props,
        ...omit(option ?? {}, 'label'),
      }
      if (!tagProps.color) {
        tagProps.color = colors[index % colors.length]
      }

      return h(Tag, tagProps, { default: () => option?.label ?? value })
    },
  })

  // 文本单元格，统一使用项目 Text 组件处理省略展示。
  vxeUI.renderer.add('CellText', {
    renderTableDefault({ props }: any, { column, row }: any) {
      const textProps = isFunction(props) ? props?.({ column, row }) : props
      const value = get(row, column.field)
      return <Text content={value} lineClamp={1} {...textProps}></Text>
    },
  })
}
